import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db, COLLECTIONS } from '@/lib/firebase';
import { Laboratory } from '@/types/product';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AdminLaboratories = () => {
  const [laboratories, setLaboratories] = useState<Laboratory[]>([]);
  const [form, setForm] = useState<Omit<Laboratory, 'id'>>({
    name: '',
    logo: '',
    description: '',
    country: '',
    website: ''
  });
  const [selectedLab, setSelectedLab] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLaboratories();
  }, []);

  const fetchLaboratories = async () => {
    const snapshot = await getDocs(collection(db, COLLECTIONS.LABORATORIES));
    const labs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Laboratory[];
    setLaboratories(labs);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setLogoFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    let logoUrl = form.logo;
    if (logoFile) {
      // Upload du logo dans Firebase Storage
      const storageRef = (await import('firebase/storage')).ref;
      const { uploadBytes, getDownloadURL } = await import('firebase/storage');
      const storage = (await import('@/lib/firebase')).storage;
      const fileRef = storageRef(storage, `laboratories/${Date.now()}_${logoFile.name}`);
      const snapshot = await uploadBytes(fileRef, logoFile);
      logoUrl = await getDownloadURL(snapshot.ref);
    }
    if (selectedLab) {
      // Update
      await updateDoc(doc(db, COLLECTIONS.LABORATORIES, selectedLab), {
        ...form,
        logo: logoUrl
      });
    } else {
      // Add
      await addDoc(collection(db, COLLECTIONS.LABORATORIES), {
        ...form,
        logo: logoUrl
      });
    }
    setForm({ name: '', logo: '', description: '', country: '', website: '' });
    setLogoFile(null);
    setSelectedLab(null);
    await fetchLaboratories();
    setLoading(false);
  };

  const handleEdit = (lab: Laboratory) => {
    setForm({
      name: lab.name,
      logo: lab.logo,
      description: lab.description,
      country: lab.country,
      website: lab.website || ''
    });
    setSelectedLab(lab.id);
  };

  const handleDelete = async (id: string) => {
    await deleteDoc(doc(db, COLLECTIONS.LABORATORIES, id));
    await fetchLaboratories();
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <Button variant="ghost" className="mb-6" onClick={() => navigate('/admin/dashboard')}>← Retour au dashboard</Button>
      <h1 className="text-3xl font-bold mb-8">Gestion des Laboratoires</h1>
      <form onSubmit={handleSubmit} className="mb-8 space-y-4 bg-white p-6 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Nom du laboratoire</Label>
            <Input name="name" value={form.name} onChange={handleInputChange} required />
          </div>
          <div>
            <Label>Pays</Label>
            <Input name="country" value={form.country} onChange={handleInputChange} required />
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <textarea name="description" value={form.description} onChange={handleInputChange} className="w-full border rounded p-2" required />
        </div>
        <div>
          <Label>Site web (optionnel)</Label>
          <Input name="website" value={form.website} onChange={handleInputChange} />
        </div>
        <div>
          <Label>Logo</Label>
          <Input type="file" accept="image/*" onChange={handleLogoChange} />
          {form.logo && !logoFile && (
            <img src={form.logo} alt="logo" className="h-12 mt-2" />
          )}
          {logoFile && (
            <span className="text-sm text-gray-500">{logoFile.name}</span>
          )}
        </div>
        <div className="flex gap-4">
          <Button type="submit" disabled={loading}>{selectedLab ? 'Mettre à jour' : 'Ajouter'}</Button>
          {selectedLab && (
            <Button type="button" variant="outline" onClick={() => { setForm({ name: '', logo: '', description: '', country: '', website: '' }); setSelectedLab(null); setLogoFile(null); }}>Annuler</Button>
          )}
        </div>
      </form>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {laboratories.map(lab => (
          <Card key={lab.id} className="overflow-hidden">
            <CardHeader className="flex flex-row items-center gap-4">
              <img src={lab.logo} alt={lab.name} className="h-12 w-12 object-contain rounded bg-gray-100" />
              <div>
                <CardTitle>{lab.name}</CardTitle>
                <div className="text-sm text-gray-500">{lab.country}</div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-2 text-gray-700 text-sm line-clamp-2">{lab.description}</div>
              {lab.website && <a href={lab.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm">{lab.website}</a>}
              <div className="flex gap-2 mt-4">
                <Button size="sm" onClick={() => navigate(`/admin/laboratories/${lab.id}/products`)}>Gérer la gamme de produits</Button>
                <Button size="sm" variant="outline" onClick={() => handleEdit(lab)}>Modifier</Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(lab.id)}>Supprimer</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminLaboratories; 